import { useState } from 'react';
import { passwordLengthError } from '../utils/passwordPolicy';
import { PasswordStrength } from '../components/ui/PasswordStrength';
import { apiFetch } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';

export default function EmployeeSetupPage({ token, restaurantId }) {
  usePageTitle('Account Setup');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit() {
    if (!password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordLengthError(password)) {
      setError(passwordLengthError(password));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/${restaurantId}/employees/setup`, {
        method: 'POST',
        body: JSON.stringify({ token, password, confirmPassword }),
      });
      if (res.ok) {
        setDone(true);
      } else {
        setError(res.message || 'Something went wrong.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#0f172a', padding: '24px'
    }}>
      <div style={{
        background: '#1e293b', borderRadius: '16px',
        padding: '32px', width: '100%', maxWidth: '400px',
        color: '#fff'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🍜</div>
        <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700' }}>
          {done ? 'Account Created!' : 'Set Up Your Account'}
        </h2>
        <p style={{ margin: '0 0 24px', color: '#94a3b8', fontSize: '14px' }}>
          {done
            ? 'You can now log in with your email and password.'
            : 'Create a password to access your schedule.'}
        </p>

        <p style={{ fontSize: 14, color: '#cbd5e1' }}>Read our <a href="/legal" target="_blank" rel="noreferrer" style={{ color: '#fcd34d' }}>Terms of Use and Privacy &amp; Attendance Notice</a>. After signing in, you will review them before entering personal details.</p>
        {done ? (
          <a
            href="/"
            style={{
              display: 'block', textAlign: 'center',
              background: 'linear-gradient(135deg,#f59e0b,#d97706)',
              color: '#fff', textDecoration: 'none',
              padding: '14px', borderRadius: '10px',
              fontWeight: '700', fontSize: '15px'
            }}
          >
            Go to Login →
          </a>
        ) : (
          <form onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="password"
                style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="At least 15 characters"
                autoComplete="new-password"
                style={{
                  width: '100%', padding: '12px',
                  background: '#0f172a', border: '1px solid #334155',
                  borderRadius: '8px', color: '#fff', fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <PasswordStrength value={password} />
            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="confirmPassword"
                style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Repeat password"
                style={{
                  width: '100%', padding: '12px',
                  background: '#0f172a', border: '1px solid #334155',
                  borderRadius: '8px', color: '#fff', fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', padding: '10px 14px',
                color: '#991b1b', fontSize: '13px', marginBottom: '16px'
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '14px',
                background: loading ? '#64748b' : 'linear-gradient(135deg,#f59e0b,#d97706)',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontWeight: '700', fontSize: '15px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
